import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { AddressAutocomplete } from "@/components/properties/AddressAutocomplete";
import { usStates } from "@/utils/states";

export interface PropertyDetails {
  property_address: string;
  city: string;
  state: string;
  zip_code: string;
  property_type: string;
  bedrooms: string;
  bathrooms: string;
  square_feet: string;
  year_built: string;
  condition: string;
  asking_price: string;
}

const emptyDetails = (): PropertyDetails => ({
  property_address: "",
  city: "",
  state: "",
  zip_code: "",
  property_type: "",
  bedrooms: "",
  bathrooms: "",
  square_feet: "",
  year_built: "",
  condition: "",
  asking_price: "",
});

const PROPERTY_TYPES = [
  "Single Family",
  "Multi-Family",
  "Townhouse",
  "Condo",
  "Duplex",
  "Triplex",
  "Quadplex",
  "Mobile Home",
  "Land",
  "Commercial",
  "Other",
];

const CONDITIONS = ["Excellent", "Good", "Fair", "Poor", "Needs Full Rehab"];

interface PropertyDetailsStepProps {
  initialDetails?: PropertyDetails;
  onBack: () => void;
  onNext: (details: PropertyDetails) => void;
}

const PropertyDetailsStep = ({ initialDetails, onBack, onNext }: PropertyDetailsStepProps) => {
  const [details, setDetails] = useState<PropertyDetails>(initialDetails || emptyDetails());

  const update = (field: keyof PropertyDetails, value: string) => {
    setDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressSelect = (addr: { address: string; city: string; state: string; zipCode: string }) => {
    setDetails((prev) => ({
      ...prev,
      property_address: addr.address,
      city: addr.city,
      state: addr.state,
      zip_code: addr.zipCode,
    }));
  };

  const validate = (): boolean => {
    if (!details.property_address.trim()) {
      toast({ title: "Property address is required", variant: "destructive" });
      return false;
    }
    if (!details.city.trim()) {
      toast({ title: "City is required", variant: "destructive" });
      return false;
    }
    if (!details.state.trim()) {
      toast({ title: "State is required", variant: "destructive" });
      return false;
    }
    if (!details.zip_code.trim()) {
      toast({ title: "ZIP code is required", variant: "destructive" });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onNext(details);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Property Details</h2>
        <p className="text-slate-400">
          Tell us about the property you'd like marketed to cash buyers.
        </p>
      </div>

      <div className="bg-card border border-border rounded-lg p-5 space-y-5">
        {/* Address */}
        <AddressAutocomplete
          onAddressSelect={handleAddressSelect}
          onInputChange={(val) => update("property_address", val)}
          initialAddress={details.property_address}
          label="Property Address *"
        />

        {/* City / State / Zip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-foreground">City *</Label>
            <Input
              value={details.city}
              onChange={(e) => update("city", e.target.value)}
              placeholder="City"
              maxLength={100}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-foreground">State *</Label>
            <select
              value={details.state}
              onChange={(e) => update("state", e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">Select state</option>
              {usStates.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label className="text-foreground">ZIP Code *</Label>
            <Input
              value={details.zip_code}
              onChange={(e) => update("zip_code", e.target.value)}
              placeholder="12345"
              maxLength={10}
            />
          </div>
        </div>

        {/* Property Type / Condition */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-foreground">Property Type</Label>
            <select
              value={details.property_type}
              onChange={(e) => update("property_type", e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">Select type</option>
              {PROPERTY_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label className="text-foreground">Condition</Label>
            <select
              value={details.condition}
              onChange={(e) => update("condition", e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">Select condition</option>
              {CONDITIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Beds / Baths / SqFt / Year Built */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-foreground">Bedrooms</Label>
            <Input
              type="number"
              min="0"
              value={details.bedrooms}
              onChange={(e) => update("bedrooms", e.target.value)}
              placeholder="3"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-foreground">Bathrooms</Label>
            <Input
              type="number"
              min="0"
              step="0.5"
              value={details.bathrooms}
              onChange={(e) => update("bathrooms", e.target.value)}
              placeholder="2"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-foreground">Sq Ft</Label>
            <Input
              type="number"
              min="0"
              value={details.square_feet}
              onChange={(e) => update("square_feet", e.target.value)}
              placeholder="1500"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-foreground">Year Built</Label>
            <Input
              type="number"
              min="1800"
              max="2030"
              value={details.year_built}
              onChange={(e) => update("year_built", e.target.value)}
              placeholder="1990"
            />
          </div>
        </div>

        {/* Asking Price */}
        <div className="space-y-2">
          <Label className="text-foreground">Asking Price</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
            <Input
              type="number"
              min="0"
              value={details.asking_price}
              onChange={(e) => update("asking_price", e.target.value)}
              placeholder="250000"
              className="pl-7"
            />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} className="border-border">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] px-8 group"
          size="lg"
        >
          Continue to Review
          <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
        </Button>
      </div>
    </div>
  );
};

export default PropertyDetailsStep;